import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useLang } from "@/i18n/LangContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function fmt(d) {
  if (!d) return "";
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

const INITIAL = {
  name: "",
  email: "",
  phone: "",
  adults: 2,
  childrenCount: 0,
  childrenAges: [],
  message: "",
};

export default function BookingForm({ range, setRange }) {
  const { t, lang } = useLang();
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState(INITIAL);

  const onChange = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const onChildrenCountChange = (val) => {
    const n = Number(val);
    setForm((s) => {
      const ages = [...s.childrenAges];
      if (n > ages.length) while (ages.length < n) ages.push("");
      else ages.length = n;
      return { ...s, childrenCount: n, childrenAges: ages };
    });
  };

  const onChildAgeChange = (idx, val) => {
    setForm((s) => {
      const ages = [...s.childrenAges];
      ages[idx] = val;
      return { ...s, childrenAges: ages };
    });
  };

  const buildMessage = () => {
    const adults = Number(form.adults);
    const kids = Number(form.childrenCount);
    const lines = [];
    lines.push(
      lang === "it"
        ? `Adulti (16+): ${adults}`
        : `Adults (16+): ${adults}`
    );
    if (kids > 0) {
      const ages = form.childrenAges
        .map((a) => (a === "" ? "?" : a))
        .join(", ");
      lines.push(
        lang === "it"
          ? `Bambini (0-16): ${kids} — età: ${ages}`
          : `Children (0-16): ${kids} — ages: ${ages}`
      );
    }
    const userMessage = form.message.trim();
    if (userMessage) lines.push("", userMessage);
    return lines.join("\n");
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!range?.from || !range?.to) {
      toast.error(t.booking.pickDates);
      const el = document.getElementById("availability");
      if (el) el.scrollIntoView({ behavior: "smooth" });
      return;
    }

    // Validate every child has an age
    if (form.childrenCount > 0 && form.childrenAges.some((a) => a === "")) {
      toast.error(
        lang === "it"
          ? "Inserisci l'età di ogni bambino"
          : "Please enter the age of every child"
      );
      return;
    }

    setLoading(true);
    try {
      const totalGuests = Number(form.adults) + Number(form.childrenCount);
      await axios.post(`${API}/bookings`, {
        name: form.name,
        email: form.email,
        phone: form.phone,
        check_in: fmt(range.from),
        check_out: fmt(range.to),
        guests: totalGuests,
        message: buildMessage(),
        language: lang,
      });
      toast.success(t.booking.successTitle, { description: t.booking.successBody });
      setForm(INITIAL);
      setRange(undefined);
    } catch (err) {
      toast.error(t.booking.errorTitle, { description: t.booking.errorBody });
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="booking"
      data-testid="booking-section"
      className="py-24 sm:py-32 bg-[#FAF9F6]"
    >
      <div className="max-w-5xl mx-auto px-6 lg:px-10">
        <div className="text-center max-w-2xl mx-auto mb-14">
          <p className="text-xs uppercase tracking-overline text-[#0097b2] mb-4">
            {t.booking.overline}
          </p>
          <h2 className="font-serif text-4xl md:text-5xl tracking-tight text-[#121C26]">
            {t.booking.title}
          </h2>
        </div>

        <form
          onSubmit={onSubmit}
          data-testid="booking-form"
          className="bg-white border border-[#E0DACE] p-8 sm:p-12 space-y-6"
        >
          <div className="grid sm:grid-cols-2 gap-6">
            <Field label={t.booking.name} required>
              <input
                data-testid="booking-name"
                required
                value={form.name}
                onChange={(e) => onChange("name", e.target.value)}
                className="input"
              />
            </Field>
            <Field label={t.booking.email} required>
              <input
                data-testid="booking-email"
                required
                type="email"
                value={form.email}
                onChange={(e) => onChange("email", e.target.value)}
                className="input"
              />
            </Field>
          </div>
          <div className="grid sm:grid-cols-3 gap-6">
            <Field label={t.booking.phone} required>
              <input
                data-testid="booking-phone"
                required
                type="tel"
                value={form.phone}
                onChange={(e) => onChange("phone", e.target.value)}
                className="input"
              />
            </Field>
            <Field label={t.booking.checkIn} required>
              <input
                data-testid="booking-checkin"
                readOnly
                value={range?.from ? fmt(range.from) : ""}
                placeholder="—"
                className="input bg-[#F0EDE6]/60 cursor-not-allowed"
              />
            </Field>
            <Field label={t.booking.checkOut} required>
              <input
                data-testid="booking-checkout"
                readOnly
                value={range?.to ? fmt(range.to) : ""}
                placeholder="—"
                className="input bg-[#F0EDE6]/60 cursor-not-allowed"
              />
            </Field>
          </div>

          {/* Guests: adults + children */}
          <div className="grid sm:grid-cols-2 gap-6">
            <Field label={t.booking.adults} hint={t.booking.adultsHint} required>
              <select
                data-testid="booking-adults"
                value={form.adults}
                onChange={(e) => onChange("adults", e.target.value)}
                className="input"
              >
                {[1, 2, 3, 4, 5, 6].map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>
            </Field>
            <Field label={t.booking.children} hint={t.booking.childrenHint}>
              <select
                data-testid="booking-children"
                value={form.childrenCount}
                onChange={(e) => onChildrenCountChange(e.target.value)}
                className="input"
              >
                {[0, 1, 2, 3, 4].map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>
            </Field>
          </div>

          {form.childrenCount > 0 && (
            <div
              data-testid="children-ages"
              className="grid sm:grid-cols-4 gap-4 p-5 bg-[#F0EDE6]/40 border border-[#E0DACE]"
            >
              {form.childrenAges.map((age, i) => (
                <Field
                  key={i}
                  label={`${t.booking.childAge} ${i + 1}`}
                  required
                >
                  <select
                    data-testid={`child-age-${i}`}
                    required
                    value={age}
                    onChange={(e) => onChildAgeChange(i, e.target.value)}
                    className="input"
                  >
                    <option value="">—</option>
                    {Array.from({ length: 17 }, (_, n) => n).map((n) => (
                      <option key={n} value={n}>
                        {n} {t.booking.years}
                      </option>
                    ))}
                  </select>
                </Field>
              ))}
            </div>
          )}

          <Field label={t.booking.message}>
            <textarea
              data-testid="booking-message"
              rows={4}
              placeholder={t.booking.messagePlaceholder}
              value={form.message}
              onChange={(e) => onChange("message", e.target.value)}
              className="input resize-none"
            />
          </Field>

          <div
            data-testid="booking-disclaimer"
            className="border-l-2 border-[#0097b2] bg-[#F0EDE6]/60 px-5 py-4 text-sm text-[#4A5D6B] leading-relaxed"
          >
            {t.booking.disclaimer}
          </div>

          <button
            type="submit"
            disabled={loading}
            data-testid="booking-submit"
            className="w-full sm:w-auto inline-flex items-center justify-center px-8 py-4 rounded-full bg-[#1A3B4D] text-[#FAF9F6] text-sm tracking-wide hover:bg-[#102632] transition-colors disabled:opacity-60"
          >
            {loading ? t.booking.sending : t.booking.submit}
            {!loading && <span className="ml-2">→</span>}
          </button>
        </form>
      </div>

      <style>{`
        .input { width:100%; padding: 0.85rem 1rem; background:#FAF9F6; border:1px solid #E0DACE; color:#121C26; font-family:'Outfit',sans-serif; font-size:0.95rem; outline:none; transition: border .2s, background .2s; }
        .input:focus { border-color:#1A3B4D; background:#fff; }
      `}</style>
    </section>
  );
}

function Field({ label, hint, required, children }) {
  return (
    <label className="block">
      <span className="flex items-baseline gap-2 mb-2">
        <span className="text-xs uppercase tracking-overline text-[#0097b2]">
          {label}
          {required && <span className="text-[#1A3B4D]"> *</span>}
        </span>
        {hint && (
          <span className="text-[10px] text-[#4A5D6B]/70 normal-case tracking-normal">
            ({hint})
          </span>
        )}
      </span>
      {children}
    </label>
  );
}
